import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantConfig } from '../../../hooks/useTenantConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Users, 
  Target, 
  Briefcase, 
  Calendar,
  Palette,
  Settings,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Globe,
  Phone,
  Mail,
  MapPin,
  Clock,
  DollarSign,
  Languages,
  Eye,
  EyeOff,
  Upload,
  Shield,
  Zap,
  BarChart3,
  Key,
  Bell,
  Link as LinkIcon,
  X
} from 'lucide-react';
import './TenantSettings.css';

const TenantSettings = () => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const { token, user } = useAuth();
  const { refetch: refetchTenantConfig } = useTenantConfig();
  const { isDark } = useTheme();
  const { t } = useTranslation();

  // Company information form state
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    companyLogo: '',
    primaryContact: '',
    phoneNumber: '',
    address: '',
    website: '',
    timezone: 'UTC',
    currency: 'USD',
    language: 'en'
  });

  // Theme form state
  const [themeForm, setThemeForm] = useState({
    brandColors: {
      primary: '#007bff',
      secondary: '#6c757d',
      accent: '#28a745'
    },
    features: {
      enableNotifications: true,
      enableIntegrations: true,
      enableCustomReports: false,
      enableAPIAccess: false
    }
  });

  useEffect(() => {
    loadTenantInfo();
    loadStatistics();
  }, []);

  const loadTenantInfo = async () => {
    try {
      const response = await fetch('/api/tenant-admin/info', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Admin role required.');
        }
        throw new Error('Failed to load tenant information');
      }

      const data = await response.json();
      setTenantInfo(data.tenant);
      
      const config = data.tenant.configuration || {};
      setCompanyForm({
        companyName: data.tenant.name || '',
        companyLogo: config.company_logo || '',
        primaryContact: config.primary_contact || '',
        phoneNumber: config.phone_number || '',
        address: config.address || '',
        website: config.website || '',
        timezone: config.timezone || 'UTC',
        currency: config.currency || 'USD',
        language: config.language || 'en'
      });

      setThemeForm({
        brandColors: config.brand_colors || {
          primary: '#007bff',
          secondary: '#6c757d',
          accent: '#28a745'
        },
        features: config.features || {
          enableNotifications: true,
          enableIntegrations: true,
          enableCustomReports: false,
          enableAPIAccess: false
        }
      });

    } catch (error) {
      console.error('Error loading tenant info:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/tenant-admin/statistics', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleCompanyInfoSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/tenant-admin/company-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update company information');
      }

      const data = await response.json();
      setSuccessMessage('Company information updated successfully!');
      
      // Update tenant info
      setTenantInfo(prev => ({
        ...prev,
        name: data.tenant.name,
        configuration: {
          ...prev.configuration,
          ...data.tenant.configuration
        }
      }));

      // Refresh tenant config in header immediately
      console.log('🔄 Refreshing tenant config after company info save...');
      refetchTenantConfig();

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating company info:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleThemeSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/tenant-admin/theme', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(themeForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update theme settings');
      }

      setSuccessMessage('Theme settings updated successfully!');
      
      // Refresh tenant config in header immediately
      console.log('🔄 Refreshing tenant config after theme save...');
      refetchTenantConfig();
      
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating theme:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">
            {t('tenantSettings.loading', 'Loading tenant settings...')}
          </p>
        </div>
      </div>
    );
  }

  if (error && !tenantInfo) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('tenantSettings.accessError.title', 'Access Error')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('tenantSettings.accessError.contactAdmin', 'Please contact your system administrator if you believe you should have access to this page.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl">
                  <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {t('tenantSettings.title', 'Tenant Settings')}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    {t('tenantSettings.subtitle', 'Manage your organization\'s information and settings')}
                  </p>
                </div>
              </div>
              {tenantInfo && (
                <div className="flex items-center space-x-3">
                  <div className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">
                    {tenantInfo.code}
                  </div>
                  <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm">
                    {tenantInfo.name}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
                <p className="text-red-800 dark:text-red-200 font-medium">{error}</p>
              </div>
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600 dark:hover:text-red-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                <p className="text-green-800 dark:text-green-200 font-medium">{successMessage}</p>
              </div>
              <button 
                onClick={() => setSuccessMessage('')}
                className="text-green-400 hover:text-green-600 dark:hover:text-green-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>{t('tenantSettings.tabs.overview', 'Overview')}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('company')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'company'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4" />
                  <span>{t('tenantSettings.tabs.company', 'Company Info')}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('theme')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'theme'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Palette className="h-4 w-4" />
                  <span>{t('tenantSettings.tabs.theme', 'Theme & Features')}</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Statistics Cards */}
                {statistics && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      {t('tenantSettings.overview.title', 'Tenant Overview')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-blue-100 text-sm font-medium">{t('tenantSettings.overview.activeUsers', 'Active Users')}</p>
                            <p className="text-3xl font-bold">{statistics.totalUsers}</p>
                          </div>
                          <Users className="h-8 w-8 text-blue-200" />
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-100 text-sm font-medium">{t('tenantSettings.overview.totalCustomers', 'Total Customers')}</p>
                            <p className="text-3xl font-bold">{statistics.totalCustomers}</p>
                          </div>
                          <Target className="h-8 w-8 text-green-200" />
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-purple-100 text-sm font-medium">{t('tenantSettings.overview.opportunities', 'Opportunities')}</p>
                            <p className="text-3xl font-bold">{statistics.totalOpportunities}</p>
                          </div>
                          <Briefcase className="h-8 w-8 text-purple-200" />
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-orange-100 text-sm font-medium">{t('tenantSettings.overview.activeTasks', 'Active Tasks')}</p>
                            <p className="text-3xl font-bold">{statistics.totalActivities}</p>
                          </div>
                          <Calendar className="h-8 w-8 text-orange-200" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tenant Details */}
                {tenantInfo && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                      {t('tenantSettings.overview.tenantDetails', 'Tenant Details')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('tenantSettings.overview.tenantCode', 'Tenant Code')}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {tenantInfo.code}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('tenantSettings.overview.organizationName', 'Organization Name')}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {tenantInfo.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('tenantSettings.overview.status', 'Status')}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            tenantInfo.isActive 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {tenantInfo.isActive ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {t('tenantSettings.overview.active', 'Active')}
                              </>
                            ) : (
                              <>
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {t('tenantSettings.overview.inactive', 'Inactive')}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('tenantSettings.overview.description', 'Description')}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white text-right max-w-xs">
                            {tenantInfo.description || t('tenantSettings.overview.noDescription', 'No description set')}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('tenantSettings.overview.created', 'Created')}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {new Date(tenantInfo.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'company' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t('tenantSettings.company.title', 'Company Information')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('tenantSettings.company.subtitle', 'Update your organization\'s basic information and contact details.')}
                  </p>
                </div>
                
                <form onSubmit={handleCompanyInfoSave} className="space-y-8">
                  {/* Basic Information */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                      {t('tenantSettings.company.basicInfo', 'Basic Information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.companyName', 'Company Name')} *
                        </label>
                        <input
                          type="text"
                          id="companyName"
                          value={companyForm.companyName}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, companyName: e.target.value }))}
                          required
                          maxLength={200}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                          placeholder={t('tenantSettings.company.companyNamePlaceholder', 'Enter your company name')}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="companyLogo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.companyLogo', 'Company Logo URL')}
                        </label>
                        <div className="relative">
                          <input
                            type="url"
                            id="companyLogo"
                            value={companyForm.companyLogo}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, companyLogo: e.target.value }))}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                            placeholder="https://example.com/logo.png"
                          />
                          <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Phone className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                      {t('tenantSettings.company.contactInfo', 'Contact Information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="primaryContact" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.primaryContact', 'Primary Contact')}
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="primaryContact"
                            value={companyForm.primaryContact}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, primaryContact: e.target.value }))}
                            maxLength={100}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                            placeholder={t('tenantSettings.company.primaryContactPlaceholder', 'Enter primary contact name')}
                          />
                          <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.phoneNumber', 'Phone Number')}
                        </label>
                        <div className="relative">
                          <input
                            type="tel"
                            id="phoneNumber"
                            value={companyForm.phoneNumber}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                            maxLength={20}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                            placeholder={t('tenantSettings.company.phoneNumberPlaceholder', '+1 (555) 123-4567')}
                          />
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.address', 'Address')}
                        </label>
                        <div className="relative">
                          <textarea
                            id="address"
                            value={companyForm.address}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, address: e.target.value }))}
                            rows={3}
                            maxLength={500}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors resize-none"
                            placeholder={t('tenantSettings.company.addressPlaceholder', 'Enter your company address')}
                          />
                          <MapPin className="absolute left-3 top-4 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.website', 'Website')}
                        </label>
                        <div className="relative">
                          <input
                            type="url"
                            id="website"
                            value={companyForm.website}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                            placeholder="https://example.com"
                          />
                          <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Regional Settings */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Globe className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                      {t('tenantSettings.company.regionalSettings', 'Regional Settings')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.timezone', 'Timezone')}
                        </label>
                        <div className="relative">
                          <select
                            id="timezone"
                            value={companyForm.timezone}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, timezone: e.target.value }))}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors appearance-none"
                          >
                            <option value="UTC">UTC</option>
                            <option value="Asia/Bangkok">Asia/Bangkok</option>
                            <option value="America/New_York">America/New_York</option>
                            <option value="Europe/London">Europe/London</option>
                            <option value="Asia/Tokyo">Asia/Tokyo</option>
                          </select>
                          <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.currency', 'Currency')}
                        </label>
                        <div className="relative">
                          <select
                            id="currency"
                            value={companyForm.currency}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, currency: e.target.value }))}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors appearance-none"
                          >
                            <option value="USD">USD</option>
                            <option value="THB">THB</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="JPY">JPY</option>
                          </select>
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tenantSettings.company.language', 'Language')}
                        </label>
                        <div className="relative">
                          <select
                            id="language"
                            value={companyForm.language}
                            onChange={(e) => setCompanyForm(prev => ({ ...prev, language: e.target.value }))}
                            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors appearance-none"
                          >
                            <option value="en">English</option>
                            <option value="th">Thai</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                          </select>
                          <Languages className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button 
                      type="submit" 
                      disabled={saving}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t('tenantSettings.company.saving', 'Saving...')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {t('tenantSettings.company.save', 'Save Company Info')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'theme' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t('tenantSettings.theme.title', 'Theme & Features')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('tenantSettings.theme.subtitle', 'Customize your organization\'s branding and enable features.')}
                  </p>
                </div>
                
                <form onSubmit={handleThemeSave} className="space-y-8">
                  {/* Brand Colors */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Palette className="h-5 w-5 mr-2 text-pink-600 dark:text-pink-400" />
                      {t('tenantSettings.theme.brandColors', 'Brand Colors')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('tenantSettings.theme.primaryColor', 'Primary Color')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            id="primaryColor"
                            value={themeForm.brandColors.primary}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, primary: e.target.value }
                            }))}
                            className="w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={themeForm.brandColors.primary}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, primary: e.target.value }
                            }))}
                            pattern="^#[0-9A-Fa-f]{6}$"
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="#007bff"
                          />
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('tenantSettings.theme.secondaryColor', 'Secondary Color')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            id="secondaryColor"
                            value={themeForm.brandColors.secondary}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, secondary: e.target.value }
                            }))}
                            className="w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={themeForm.brandColors.secondary}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, secondary: e.target.value }
                            }))}
                            pattern="^#[0-9A-Fa-f]{6}$"
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="#6c757d"
                          />
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <label htmlFor="accentColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('tenantSettings.theme.accentColor', 'Accent Color')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            id="accentColor"
                            value={themeForm.brandColors.accent}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, accent: e.target.value }
                            }))}
                            className="w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={themeForm.brandColors.accent}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              brandColors: { ...prev.brandColors, accent: e.target.value }
                            }))}
                            pattern="^#[0-9A-Fa-f]{6}$"
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                            placeholder="#28a745"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                      {t('tenantSettings.theme.features', 'Features')}
                    </h3>
                    <div className="space-y-6">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="enableNotifications"
                            checked={themeForm.features.enableNotifications}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              features: { ...prev.features, enableNotifications: e.target.checked }
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <label htmlFor="enableNotifications" className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                              <Bell className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                              {t('tenantSettings.theme.enableNotifications', 'Enable Notifications')}
                            </label>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {t('tenantSettings.theme.enableNotificationsDesc', 'Allow users to receive email and in-app notifications')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="enableIntegrations"
                            checked={themeForm.features.enableIntegrations}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              features: { ...prev.features, enableIntegrations: e.target.checked }
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <label htmlFor="enableIntegrations" className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                              <Zap className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400" />
                              {t('tenantSettings.theme.enableIntegrations', 'Enable Integrations')}
                            </label>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {t('tenantSettings.theme.enableIntegrationsDesc', 'Allow third-party integrations and webhooks')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="enableCustomReports"
                            checked={themeForm.features.enableCustomReports}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              features: { ...prev.features, enableCustomReports: e.target.checked }
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <label htmlFor="enableCustomReports" className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                              <BarChart3 className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                              {t('tenantSettings.theme.enableCustomReports', 'Enable Custom Reports')}
                            </label>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {t('tenantSettings.theme.enableCustomReportsDesc', 'Allow users to create custom reports and analytics')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="enableAPIAccess"
                            checked={themeForm.features.enableAPIAccess}
                            onChange={(e) => setThemeForm(prev => ({
                              ...prev,
                              features: { ...prev.features, enableAPIAccess: e.target.checked }
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <label htmlFor="enableAPIAccess" className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                              <Key className="h-4 w-4 mr-2 text-red-600 dark:text-red-400" />
                              {t('tenantSettings.theme.enableAPIAccess', 'Enable API Access')}
                            </label>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {t('tenantSettings.theme.enableAPIAccessDesc', 'Allow API access for external applications')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button 
                      type="submit" 
                      disabled={saving}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t('tenantSettings.theme.saving', 'Saving...')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {t('tenantSettings.theme.save', 'Save Theme Settings')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;